// subscriptionPlanV2.helper.js
//
// Pure, framework-agnostic helpers for the Subscription Plan V2 payload.
//
// Why a separate file from SubscriptionUtils:
// V1 plans store services as a fixed, flat shape (`features.premise.limit`,
// `features.geofence.range`, etc.) hard-coded to a handful of known keys.
// V2 plans are driven entirely by whatever `getDefaultPermissions()` returns,
// so the number/shape of permissions can change without touching this file
// or the modal. Keeping the two normalizers separate avoids one giant
// function trying to branch on plan version.

const RANGE_APPLICABLE_LABELS = ['premise', 'geofence'];
const LIMIT_APPLICABLE_LABELS = ['premise', 'geofence', 'edec', 'user'];

export const isRangeApplicable = (label) => RANGE_APPLICABLE_LABELS.includes(label);
export const isLimitApplicable = (label) => LIMIT_APPLICABLE_LABELS.includes(label);

/**
 * A permission is "toggleable" when the API actually exposes booleans for
 * it (isView/isCreate/isManage/isReport). Permissions like `distributor`,
 * `customer`, and `group` come back with all four flags as `null`, meaning
 * they aren't configurable per-plan — we render nothing for them and pass
 * them through untouched in the payload.
 */
export const isPermissionToggleable = (permission) => {
  if (!permission) return false;
  return (
    permission.isView !== null ||
    permission.isCreate !== null ||
    permission.isManage !== null ||
    permission.isReport !== null
  );
};

/**
 * Reads a permission's "create" flag, tolerating the `isCraete` typo that
 * exists on the `declaration` entry in some environments' seed data.
 */
const readIsCreate = (source) => {
  if (!source) return null;
  if (source.isCreate !== undefined) return source.isCreate;
  if (source.isCraete !== undefined) return source.isCraete;
  return null;
};

/**
 * Builds the initial per-permission form state, keyed by label.
 *
 * - Create flow: seeded straight from getDefaultPermissions().
 * - Edit flow: seeded from the plan's existing `features` array so the
 *   modal auto-populates; falls back to defaultPermissions for any label
 *   missing on the plan (e.g. a permission added after the plan was created).
 *
 */
export const getInitialPermissionValues = (defaultPermissions = [], existingFeatures = []) => {
  const existingByLabel = existingFeatures.reduce((acc, feature) => {
    if (feature?.label) acc[feature.label] = feature;
    return acc;
  }, {});
  console.log(defaultPermissions, 'defaultPermissions');
  return defaultPermissions.reduce((acc, permission) => {
    const existing = existingByLabel[permission.label];
    const source = existing || permission;
    const toggleable = isPermissionToggleable(permission);

    acc[permission.label] = {
      isView: toggleable ? !!source.isView : null,
      isCreate: toggleable ? !!readIsCreate(source) : null,
      isManage: toggleable ? !!source.isManage : null,
      isReport: toggleable ? !!source.isReport : null,
      range: isRangeApplicable(permission.label)
        ? source.range === undefined
          ? null
          : source.range
        : null,
      limit: isLimitApplicable(permission.label)
        ? source.limit === undefined
          ? null
          : source.limit
        : null,
    };
    return acc;
  }, {});
};

/**
 * Builds the `features` array for the create/update payload.
 * `defaultPermissions` is treated as the source of truth for
 * label/description/order/limit; `permissionValues` supplies only the
 * fields the admin can actually edit.
 */
const buildFeaturesPayload = (defaultPermissions = [], permissionValues = {}) => {
  return defaultPermissions.map((permission) => {
    const toggleable = isPermissionToggleable(permission);
    const rangeApplicable = isRangeApplicable(permission.label);
    const limitApplicable = isLimitApplicable(permission.label);
    const values = permissionValues[permission.label] || {};

    const rawRange = values.range;
    const rawLimit = values.limit;
    const range =
      !rangeApplicable || rawRange === '' || rawRange === null || rawRange === undefined
        ? null
        : Number(rawRange);

    const limit =
      !limitApplicable || rawLimit === '' || rawLimit === null || rawLimit === undefined
        ? null
        : Number(rawLimit);

    return {
      label: permission.label,
      description: permission.description,
      isView: toggleable ? !!values.isView : null,
      isReport: toggleable ? !!values.isReport : null,
      isManage: toggleable ? !!values.isManage : null,
      isCreate: toggleable ? !!values.isCreate : null,
      limit,
      range,
    };
  });
};

/**
 * Normalizes the raw form values + permission toggle state into the final
 * create/update subscription plan (V2) API payload.
 *
 * @param {object} params
 * @param {object} params.formValues - values from the react-form-ula Form
 *   (description, labelName, billingPeriod, baseCost, templateId, ...)
 * @param {Array} params.defaultPermissions - result of getDefaultPermissions()
 * @param {object} params.permissionValues - state from getInitialPermissionValues,
 *   mutated by the user via the toggle/range UI
 * @param {Array} params.planFields - [{ id, value }] rows from AddPlanDetails
 * @param {boolean} params.isIncremental - value of the "Multiple Properties" switch
 * @param {string} [params.templateId] - selected email template id, if changed
 *   via the dropdown (falls back to formValues.templateId otherwise)
 */
export const normalizePlanV2 = ({
  formValues,
  defaultPermissions,
  permissionValues,
  planFields,
  isIncremental,
  templateId,
}) => {
  return {
    ...formValues,
    templateId: templateId ?? formValues?.templateId,
    baseCost:
      formValues?.baseCost !== undefined && formValues?.baseCost !== ''
        ? Number(formValues.baseCost)
        : formValues?.baseCost,
    isIncremental: isIncremental ? 1 : 0,
    featureList: (planFields || [])
      .filter((field) => field.value?.trim())
      .map((field) => field.value.trim()),
    features: buildFeaturesPayload(defaultPermissions, permissionValues),
  };
};

export const getFeatureValue = (features, label, key) => {
  const feature = features?.find((item) => item.label === label);

  return feature?.[key] ?? '--';
};
